import { fetchPublicDocument } from "@/api/documents"
import personalFallback from "@/data/personal.json"
import type { PersonalDocument } from "@/types/personalDocument"

const CACHE_KEY = "daks_legal_document_privacy"
const CACHE_TTL_MS = 60 * 60 * 1000

type CachedPrivacy = PersonalDocument & {
  revision: number
  fetchedAt: number
}

const fallbackDoc = (): PersonalDocument => ({
  title: personalFallback.title,
  blocks: personalFallback.blocks as PersonalDocument["blocks"],
})

const readCache = (): CachedPrivacy | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedPrivacy
    if (!parsed?.title || !Array.isArray(parsed.blocks)) return null
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

const writeCache = (doc: CachedPrivacy): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(doc))
  } catch {
    // ignore
  }
}

export async function loadPrivacyDocumentWithMeta(): Promise<{
  document: PersonalDocument
  revision: number | null
}> {
  const cached = readCache()
  if (cached) {
    return {
      document: { title: cached.title, blocks: cached.blocks },
      revision: cached.revision,
    }
  }

  try {
    const data = await fetchPublicDocument("privacy")
    writeCache({
      title: data.title,
      blocks: data.blocks,
      revision: data.revision,
      fetchedAt: Date.now(),
    })
    return {
      document: { title: data.title, blocks: data.blocks },
      revision: data.revision,
    }
  } catch {
    return { document: fallbackDoc(), revision: null }
  }
}

export async function getPrivacyDocumentRevision(): Promise<number | null> {
  const { revision } = await loadPrivacyDocumentWithMeta()
  return revision
}
